import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bot, ChevronRight, RefreshCw, CheckCircle2 } from 'lucide-react';
import { api } from '../lib/api';
import { visibleGroups, useNavAccess } from '../lib/nav';
import { askCopilot, COPILOT_SUGGESTIONS } from '../components/CopilotPanel';
import { easternDay } from '../../modules/q2c/eastern-day.mjs';

const TONE = {
  bad: 'border-l-red-500',
  warn: 'border-l-amber-500',
  info: 'border-l-sky-500',
};

function me() {
  try {
    const p = JSON.parse(atob(localStorage.getItem('mickey_token').split('.')[1]));
    const e = String(p.email || '').toLowerCase();
    return { 'chad@vas4construction.com': 'Chad', 'steve.parker@concria.com': 'Steve', 'chase.parker@concria.com': 'Chase' }[e] || null;
  } catch { return null; }
}

function money(n) {
  return `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function receivablesLine(r) {
  if (!r) return '';
  if (r.invoices_cents && r.books_cents) return `Owed to CCS ${money(r.total_cents / 100)} (from invoices ${money(r.invoices_cents / 100)}, from books ${money(r.books_cents / 100)})`;
  return `Owed to CCS ${money(r.total_cents / 100)}${r.source ? ` ${r.source}` : ''}`;
}

function summaryLine(s) {
  if (s.key === 'invoices') {
    const due = s.count ? `${s.overdue_count} overdue (${money(s.overdue_total)}), ${s.count} due within a week or overdue` : '';
    return [receivablesLine(s.receivables), due].filter(Boolean).join('. ') || null;
  }
  if (s.key === 'shipments') return `${s.count} of ${s.active_count} active ${s.active_count === 1 ? 'shipment' : 'shipments'} ${s.count === 1 ? 'needs' : 'need'} a look`;
  if (s.key === 'concria_oy' && s.count) return `${s.disputed_count} disputed, ${s.count - s.disputed_count} awaiting an answer`;
  if (s.key === 'deposits' && s.count) return `${money((s.total_cents || 0) / 100)} in ${s.count} deposit${s.count === 1 ? '' : 's'} to match`;
  if (s.key === 'tasks') return `${s.count} of yours due${s.others_count ? `, ${s.others_count} for the others` : ''}`;
  return null;
}

function Section({ s }) {
  // A part that failed to load is not "nothing here"; the note says what is missing.
  const clear = s.available && !s.count && !s.note && !(s.key === 'tasks' && s.others_count);
  return (
    <section className="min-w-0 rounded-lg border border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-800">
      <header className="flex items-center justify-between gap-2 border-b border-gray-100 px-4 py-3 dark:border-slate-700">
        <div className="min-w-0">
          <h2 className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">{s.label}</h2>
          {s.available && summaryLine(s) && <p className="break-words text-xs text-gray-500 dark:text-gray-400">{summaryLine(s)}</p>}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {s.available && (
            <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${s.count ? 'bg-primary/10 text-primary' : 'bg-gray-100 text-gray-500 dark:bg-slate-700 dark:text-gray-300'}`}>{s.count}</span>
          )}
          <Link to={s.href} className="flex items-center text-xs font-medium text-primary">Open<ChevronRight size={14} /></Link>
        </div>
      </header>
      <div className="px-2 py-2">
        {!s.available && <p className="px-2 py-2 text-xs text-gray-500 dark:text-gray-400">{s.note}</p>}
        {clear && <p className="flex items-center gap-1.5 px-2 py-2 text-xs text-gray-500 dark:text-gray-400"><CheckCircle2 size={14} className="text-emerald-500" /> Nothing here today.</p>}
        {s.available && s.items?.length > 0 && (
          <ul className="space-y-1">
            {s.items.map((i) => (
              <li key={`${i.kind}-${i.id}`}>
                <Link to={i.href} className={`block rounded-md border-l-[3px] px-3 py-2 hover:bg-gray-50 dark:hover:bg-slate-700/50 ${TONE[i.tone] || 'border-l-gray-300 dark:border-l-slate-600'}`}>
                  <div className="break-words text-sm font-medium text-gray-900 dark:text-gray-100">{i.title}</div>
                  <div className="break-words text-xs text-gray-500 dark:text-gray-400">{i.detail}</div>
                  {i.ready && (
                    <div className="mt-1 break-words text-xs text-gray-500 dark:text-gray-400" title={i.ready.rule}>
                      <span className="rounded-full bg-emerald-100 px-2 py-0.5 font-semibold text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300">Ready for approval</span>
                      {' '}{String(i.ready.by || '').split('@')[0] ? `marked by ${String(i.ready.by).split('@')[0]} ` : 'marked '}{easternDay(i.ready.at)}
                      {` · ${i.ready.partners > 1 ? 'two partners approve (over $25K)' : 'one partner approves'}`}
                    </div>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        )}
        {s.available && s.more > 0 && <Link to={s.href} className="block px-3 py-1.5 text-xs font-medium text-primary">and {s.more} more</Link>}
        {s.available && s.note && <p className="px-3 py-1 text-xs text-amber-600">{s.note}</p>}
      </div>
    </section>
  );
}

export default function HomePage() {
  const navAccess = useNavAccess();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [ask, setAsk] = useState('');
  const name = me();

  async function load() {
    setLoading(true);
    setError('');
    try { setData(await api.get('/home/attention')); } catch (e) { setError(e.message || 'Could not load'); }
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  const when = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  const hour = new Date().getHours();
  const hello = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
  const sections = data?.sections || [];
  const total = sections.filter((s) => s.available).reduce((n, s) => n + (s.count || 0), 0);

  return (
    <div className="mx-auto max-w-6xl">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-2">
        <div className="min-w-0">
          <p className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">{when}</p>
          <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100 sm:text-2xl">{hello}{name ? `, ${name}` : ''}</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">{loading ? 'Checking every module' : !data ? 'Could not check the modules' : `${total} ${total === 1 ? 'thing needs' : 'things need'} attention today`}</p>
        </div>
        <button onClick={load} className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 text-xs font-medium hover:border-primary dark:border-slate-600" disabled={loading}>
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      <form
        onSubmit={(e) => { e.preventDefault(); const q = ask.trim(); if (q && askCopilot(q)) setAsk(''); }}
        className="mb-5 rounded-lg border border-gray-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-800"
      >
        <label className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100" htmlFor="home-ask"><Bot size={16} className="text-primary" /> Ask the Copilot</label>
        <div className="flex gap-2">
          <input id="home-ask" value={ask} onChange={(e) => setAsk(e.target.value)} placeholder="What's open with Concria?" className="min-w-0 flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-primary/50 dark:border-slate-600 dark:bg-slate-900 sm:text-sm" />
          <button type="submit" className="shrink-0 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90">Ask</button>
        </div>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {COPILOT_SUGGESTIONS.slice(0, 4).map((s) => (
            <button type="button" key={s} onClick={() => { if (!askCopilot(s) && !ask.trim()) setAsk(s); }} className="rounded-full border border-gray-200 px-2.5 py-1 text-xs text-gray-600 hover:border-primary hover:text-primary dark:border-slate-600 dark:text-gray-300">{s}</button>
          ))}
        </div>
      </form>

      {error && <p className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}

      {loading && !data && (
        <div className="grid gap-4 lg:grid-cols-2">
          {[0, 1, 2, 3].map((i) => <div key={i} className="h-32 animate-pulse rounded-lg bg-gray-100 dark:bg-slate-800" />)}
        </div>
      )}

      {data && (
        <div className="grid gap-4 lg:grid-cols-2">
          {sections.filter((s) => s.available || !/do not have access/.test(s.note || '')).map((s) => <Section key={s.key} s={s} />)}
        </div>
      )}

      <h2 className="mb-3 mt-8 text-sm font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">All modules</h2>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {visibleGroups(undefined, navAccess).filter((g) => g.label !== 'Home').map((g) => (
          <div key={g.label} className="min-w-0">
            <h3 className="mb-1.5 text-xs font-semibold text-gray-900 dark:text-gray-100">{g.label}</h3>
            <div className="space-y-1">
              {g.items.map(({ to, label, icon: Icon, hint }) => (
                <Link key={to} to={to} className="flex items-center gap-2.5 rounded-lg border border-gray-200 bg-white px-3 py-2 hover:border-primary dark:border-slate-700 dark:bg-slate-800">
                  <Icon size={16} className="shrink-0 text-primary" />
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium text-gray-900 dark:text-gray-100">{label}</span>
                    {hint && <span className="block truncate text-xs text-gray-500 dark:text-gray-400">{hint}</span>}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
